"use client";

import { resolveEntitlement } from "@sum-video/shared/utils/entitlement";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { pageLoaded } from "@/lib/pageLoad";
import { loadSupabaseClient } from "@/lib/supabase/loadClient";
import { api } from "@/lib/trpc/client";
import type { Database } from "@/types/database.types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface UseAuthOptions {
  /** Waits for the page's load event before loading supabase-js. */
  afterPageLoad?: boolean;
}

export function useAuth({ afterPageLoad = false }: UseAuthOptions = {}): {
  user: SupabaseUser | null;
  loading: boolean;
  profile: Profile | null;
  isSubscribed: boolean;
  canGenerateSummary: boolean;
} {
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["auth", "user"],
    queryFn: async () => {
      if (afterPageLoad) await pageLoaded();
      const supabase = await loadSupabaseClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    },
  });

  const signedIn = Boolean(user?.id) && !user?.is_anonymous;
  const { data: profile, isFetched: profileFetched } = api.profile.get.useQuery(
    undefined,
    {
      enabled: signedIn,
    },
  );

  const isSubscribed = resolveEntitlement(profile ?? null).isSubscribed;
  const canGenerateSummary = true;

  return {
    user: user || null,
    loading: userLoading || Boolean(signedIn && !profileFetched),
    profile: profile || null,
    isSubscribed,
    canGenerateSummary,
  };
}
